import React, { useState, useRef, useEffect } from 'react';
import { Video, Sparkles, X, Clock, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

// Sequential Video Player Component - plays segments one after another
const SequentialVideoPlayer = ({ segments, darkMode }) => {
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
  const videoRef = useRef(null);

  // When a segment ends, move to the next one
  const handleSegmentEnded = () => {
    if (currentSegmentIndex < segments.length - 1) {
      setCurrentSegmentIndex(prev => prev + 1);
    } else {
      // All segments complete, restart from beginning
      setCurrentSegmentIndex(0);
    }
  };

  // Update video source when segment changes
  useEffect(() => {
    if (videoRef.current && segments[currentSegmentIndex]) {
      videoRef.current.src = segments[currentSegmentIndex];
      videoRef.current.load();
    }
  }, [currentSegmentIndex, segments]);

  return (
    <div>
      <video
        ref={videoRef}
        src={segments[currentSegmentIndex]}
        controls
        className="w-full rounded-lg"
        onEnded={handleSegmentEnded}
      />

      {segments.length > 1 && (
        <div className={`mt-2 flex items-center justify-between text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          <span>Segment {currentSegmentIndex + 1} of {segments.length}</span>
          <div className="flex gap-1">
            {segments.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSegmentIndex(idx)}
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                  idx === currentSegmentIndex
                    ? 'bg-purple-500 text-white'
                    : darkMode
                    ? 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                {idx + 1}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AITutorialGenerator = ({ darkMode, onClose, currentUser }) => {
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [totalSegments] = useState(5); // 5 segments × 12 seconds = 1 minute
  const [generatedVideos, setGeneratedVideos] = useState([]);
  const [generationStatus, setGenerationStatus] = useState('');
  const [error, setError] = useState('');
  const [segmentFiles, setSegmentFiles] = useState([]);
  const ffmpegRef = useRef(new FFmpeg());
  const [ffmpegLoaded, setFfmpegLoaded] = useState(false);

  // Load FFmpeg on component mount
  useEffect(() => {
    const loadFFmpeg = async () => {
      const ffmpeg = ffmpegRef.current;

      try {
        const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
        await ffmpeg.load({
          coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
          wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
        });
        setFfmpegLoaded(true);
        console.log('FFmpeg loaded successfully');
      } catch (error) {
        console.error('Failed to load FFmpeg:', error);
      }
    };

    loadFFmpeg();
  }, []);

  // Merge multiple video segments into one continuous video using FFmpeg
  const mergeVideos = async (videoDataUrls) => {
    if (!ffmpegLoaded) {
      throw new Error('FFmpeg not loaded yet');
    }

    const ffmpeg = ffmpegRef.current;

    try {
      console.log(`Merging ${videoDataUrls.length} video segments...`);

      // Write each video segment to FFmpeg's virtual file system
      for (let i = 0; i < videoDataUrls.length; i++) {
        const videoData = await fetchFile(videoDataUrls[i]);
        await ffmpeg.writeFile(`segment${i}.mp4`, videoData);
        console.log(`Written segment ${i + 1}/${videoDataUrls.length} to FFmpeg`);
      }

      // Create concat file list for FFmpeg
      const concatFileContent = videoDataUrls
        .map((_, i) => `file 'segment${i}.mp4'`)
        .join('\n');
      await ffmpeg.writeFile('concat_list.txt', concatFileContent);

      console.log('Starting FFmpeg merge process...');

      // Run FFmpeg concat command to merge videos
      await ffmpeg.exec([
        '-f', 'concat',
        '-safe', '0',
        '-i', 'concat_list.txt',
        '-c', 'copy',
        'merged_output.mp4'
      ]);

      console.log('FFmpeg merge complete, reading output...');

      // Read the merged video file
      const mergedData = await ffmpeg.readFile('merged_output.mp4');

      // Convert to blob and create object URL
      const blob = new Blob([mergedData.buffer], { type: 'video/mp4' });
      const objectUrl = URL.createObjectURL(blob);

      console.log('Merged video ready:', objectUrl);

      // Cleanup FFmpeg virtual file system
      try {
        for (let i = 0; i < videoDataUrls.length; i++) {
          await ffmpeg.deleteFile(`segment${i}.mp4`);
        }
        await ffmpeg.deleteFile('concat_list.txt');
        await ffmpeg.deleteFile('merged_output.mp4');
      } catch (cleanupError) {
        console.warn('Cleanup error:', cleanupError);
      }

      return objectUrl;
    } catch (error) {
      console.error('Video merge error:', error);
      throw new Error(`Failed to merge videos: ${error.message}`);
    }
  };

  // Generate tutorial video using OpenAI Sora (5 segments, 1 minute total)
  const generateTutorial = async () => {
    if (!prompt.trim()) {
      setError('Please enter a topic for your tutorial');
      return;
    }

    setIsGenerating(true);
    setError('');
    setGenerationProgress(0);
    setSegmentFiles([]);

    try {
      // Step 1: Break down topic into 5 segments using GPT
      setGenerationStatus('Planning tutorial structure...');
      setGenerationProgress(5);

      const segments = await planTutorialSegments(prompt);
      console.log('Planned segments:', segments);

      // Step 2: Generate ALL 5 video segments in PARALLEL for speed
      setGenerationStatus(`Generating all ${segments.length} segments simultaneously...`);
      setGenerationProgress(10);

      console.log(`Starting parallel generation of ${segments.length} segments...`);

      // Create array of promises to generate all segments at once
      const segmentPromises = segments.map((segment, index) =>
        generateVideoSegmentWithProgress(segment, index + 1, segments.length)
      );

      // Wait for ALL segments to complete
      const videoFiles = await Promise.all(segmentPromises);

      console.log('All segments generated successfully!');
      setGenerationProgress(75);

      // Step 3: Merge all segments into one continuous video using FFmpeg
      setGenerationStatus('Merging video segments into one file...');
      setGenerationProgress(80);

      let mergedVideoUrl;
      try {
        mergedVideoUrl = await mergeVideos(videoFiles);
        console.log('Videos merged successfully!');
      } catch (mergeError) {
        console.error('Failed to merge videos:', mergeError);
        // Fallback to sequential playback if merge fails
        console.log('Falling back to sequential playback');
        mergedVideoUrl = null;
      }

      setGenerationProgress(100);
      setGenerationStatus('Tutorial complete!');

      // Add to generated videos list with merged video or sequential playback
      setGeneratedVideos(prev => [...prev, {
        id: Date.now(),
        title: prompt,
        url: mergedVideoUrl || videoFiles[0], // Merged video or first segment
        segments: videoFiles, // Keep segments for fallback
        isMerged: !!mergedVideoUrl, // True if merge succeeded
        duration: segments.length * 12,
        createdAt: new Date().toISOString()
      }]);

      // Reset after success
      setTimeout(() => {
        setIsGenerating(false);
        setPrompt('');
      }, 2000);

    } catch (err) {
      console.error('Error generating tutorial:', err);
      setError(err.message || 'Failed to generate tutorial. Please try again.');
      setIsGenerating(false);
    }
  };

  // Plan tutorial segments using GPT
  const planTutorialSegments = async (topic) => {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/openai`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        messages: [{
          role: 'system',
          content: `You are an expert educational video scriptwriter. Your task is to create ONE cohesive lesson that flows naturally from beginning to end. The lesson will be split into exactly ${totalSegments} connected segments of 12 seconds each, forming one complete 1-minute tutorial.

IMPORTANT: This is ONE lesson broken into ${totalSegments} parts, NOT ${totalSegments} separate lessons. Each segment should build upon the previous one, like chapters in a story.`
        }, {
          role: 'user',
          content: `Create a cohesive 1-minute lesson about "${topic}" that is broken into exactly ${totalSegments} connected segments.

Structure the lesson as ONE flowing narrative:
- Segment 1: Introduction and hook (what we'll learn and why it matters)
- Segment 2: Foundation (core concept or first key point)
- Segment 3: Development (building on the foundation, second key point)
- Segment 4: Application (how to use it, practical example)
- Segment 5: Conclusion (summary and key takeaway)

Each segment should:
1. Connect to the previous segment (reference what was just learned)
2. Prepare for the next segment (hint at what's coming)
3. Be 12 seconds of content when spoken
4. Use conversational, engaging language

Format as JSON array with objects containing:
- title: Brief segment title (2-4 words)
- content: Detailed script for this 12-second segment (write it as if you're teaching)
- transition: How this segment connects to the next (except for segment 5)

Example format:
[
  {"title": "Introduction", "content": "Welcome! Today we're exploring [topic]. By the end of this minute, you'll understand [key outcome]. Let's start with the basics.", "transition": "Now that we know what we're learning, let's dive into the foundation."},
  {"title": "Core Concept", "content": "Building on that introduction, here's the fundamental idea: [explain]. This is crucial because [reason].", "transition": "With this foundation in place, let's see how it develops."},
  ...
]`
        }],
        max_tokens: 1200
      })
    });

    const data = await response.json();

    if (!data.choices || !data.choices[0]) {
      throw new Error('Invalid response from planning API');
    }

    const content = data.choices[0].message.content;

    // Parse JSON from response
    const jsonMatch = content.match(/\[[\s\S]*\]/);
    if (!jsonMatch) {
      throw new Error('Failed to parse tutorial structure');
    }

    const segments = JSON.parse(jsonMatch[0]);
    return segments.slice(0, totalSegments);
  };

  // Generate a single video segment with progress tracking (for parallel processing)
  const generateVideoSegmentWithProgress = async (segment, segmentNumber, totalCount) => {
    try {
      console.log(`[Segment ${segmentNumber}] Starting generation...`);

      const videoPrompt = `Educational video tutorial - Part ${segmentNumber} of ${totalCount}:

${segment.title}

${segment.content}

${segment.transition ? `This connects to: ${segment.transition}` : ''}

Create a clear, educational 12-second video segment. This is part of ONE continuous lesson, so the style should flow naturally with the other parts. Use text overlays, simple animations, or visual explanations. Professional educational style.`;

      // Call backend API to generate video with Sora
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/generate-video`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: videoPrompt,
          duration: '12',
          userId: currentUser?.id
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error(`[Segment ${segmentNumber}] Generation API error:`, errorData);
        throw new Error(errorData.details || errorData.error || `Failed to generate segment ${segmentNumber}`);
      }

      const data = await response.json();
      console.log(`[Segment ${segmentNumber}] Generation started with video ID: ${data.videoId}`);

      // Poll for video completion
      const videoUrl = await pollVideoCompletion(data.videoId, 300, segmentNumber);

      console.log(`[Segment ${segmentNumber}] Completed successfully!`);

      // Update UI to show this segment is done
      setSegmentFiles(prev => {
        const newFiles = [...prev];
        newFiles[segmentNumber - 1] = videoUrl;
        return newFiles;
      });

      return videoUrl;
    } catch (error) {
      console.error(`[Segment ${segmentNumber}] Failed:`, error.message);
      throw new Error(`Segment ${segmentNumber} failed: ${error.message}`);
    }
  };


  // Poll for video completion (following OpenAI documentation pattern)
  const pollVideoCompletion = async (videoId, maxAttempts = 300, segmentNumber = null) => {
    let video;
    let stuckAtProgressCount = 0;
    let lastProgress = 0;
    const segmentPrefix = segmentNumber ? `[Segment ${segmentNumber}] ` : '';

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        // Check status
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/video-status?videoId=${videoId}`,
          {
            signal: AbortSignal.timeout(55000) // 55 second timeout for the request itself
          }
        );

        if (!response.ok) {
          console.warn(`${segmentPrefix}Video status check failed (attempt ${attempt + 1}/${maxAttempts}):`, response.status);
          // Don't throw immediately, retry
          await new Promise(resolve => setTimeout(resolve, 3000));
          continue;
        }

        video = await response.json();

        // Log less frequently in parallel mode to avoid console spam (every 10 attempts)
        if (attempt % 10 === 0 || video.status === 'completed' || video.status === 'succeeded' || video.status === 'ready') {
          console.log(`${segmentPrefix}[Attempt ${attempt + 1}] Video ${videoId} - Status: "${video.status}", Progress: ${video.progress || 0}%`);
        }

        // Check if video is completed (handle different possible status values)
        if (video.status === 'completed' || video.status === 'succeeded' || video.status === 'ready') {
          console.log(`${segmentPrefix}Video generation completed! Downloading...`);

          // Download the video content
          setGenerationStatus('Downloading video...');
          const downloadResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/download-video?videoId=${videoId}`,
            {
              signal: AbortSignal.timeout(120000) // 2 minute timeout for download
            }
          );

          if (!downloadResponse.ok) {
            const errorText = await downloadResponse.text();
            console.error(`${segmentPrefix}Download failed:`, errorText);
            throw new Error(`Failed to download video: ${downloadResponse.status}`);
          }

          const downloadData = await downloadResponse.json();
          console.log(`${segmentPrefix}Video downloaded successfully`);
          return downloadData.url; // Returns data URL

        } else if (video.status === 'failed' || video.status === 'error') {
          throw new Error(video.error || 'Video generation failed');
        }

        // Check if stuck at same progress for too long
        if (video.progress === lastProgress && video.progress > 0) {
          stuckAtProgressCount++;

          // Only log warning every 20 attempts to reduce spam
          if (stuckAtProgressCount % 20 === 0) {
            console.warn(`${segmentPrefix}Progress stuck at ${video.progress}% for ${stuckAtProgressCount} attempts (status: "${video.status}")`);
          }

          // If stuck at 99-100% for 50+ attempts (2.5 minutes), try early download
          // Note: Sometimes Sora gets stuck at 99% but video is actually ready
          if (video.progress >= 99 && stuckAtProgressCount >= 50 && stuckAtProgressCount % 10 === 0) {
            console.log(`${segmentPrefix}Progress stuck at ${video.progress}% for ${stuckAtProgressCount} attempts, trying early download...`);
            try {
              const downloadResponse = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/download-video?videoId=${videoId}`,
                { signal: AbortSignal.timeout(120000) }
              );

              if (downloadResponse.ok) {
                const downloadData = await downloadResponse.json();
                console.log(`${segmentPrefix}Early download successful! Video was ready despite status being "${video.status}"`);
                return downloadData.url;
              } else {
                console.warn(`${segmentPrefix}Early download got ${downloadResponse.status}, video not ready yet - continuing to poll`);
              }
            } catch (downloadError) {
              console.warn(`${segmentPrefix}Early download failed:`, downloadError.message);
              // Continue polling - video might not be ready yet
            }
          }

          // If stuck for 150+ attempts (7.5 minutes), give up
          if (stuckAtProgressCount >= 150) {
            throw new Error(`Video generation stuck at ${video.progress}% (status: "${video.status}") for too long (${stuckAtProgressCount * 3 / 60} minutes)`);
          }
        } else {
          stuckAtProgressCount = 0;
          lastProgress = video.progress || 0;
        }

        // Don't update global progress in parallel mode - each segment updates its own
        // Individual segment progress tracking happens in generateVideoSegmentWithProgress

      } catch (fetchError) {
        console.warn(`${segmentPrefix}Fetch error on attempt ${attempt + 1}:`, fetchError.message);
        // Continue polling unless we're out of attempts
        if (attempt >= maxAttempts - 1) {
          throw new Error(`Video generation timeout after ${maxAttempts} attempts`);
        }
      }

      // Wait 3 seconds before next poll (increased from 2s)
      await new Promise(resolve => setTimeout(resolve, 3000));
    }

    throw new Error(`Video generation timeout after ${maxAttempts} attempts (${maxAttempts * 3 / 60} minutes)`);
  };


  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      generateTutorial();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className={`max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-3xl shadow-2xl ${
          darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'
        }`}
      >
        {/* Header */}
        <div className={`sticky top-0 z-10 p-6 border-b ${
          darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <Video className="w-8 h-8 text-purple-500" />
              <div>
                <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  AI Tutorial Generator
                </h2>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Create custom video tutorials with AI
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-xl transition-colors ${
                darkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Input Section */}
          <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <label className={`block mb-3 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              What topic would you like to learn? (Creates one 1-minute lesson)
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="E.g., 'Explain photosynthesis' or 'How does gravity work?' - AI will create ONE cohesive 1-minute lesson"
              disabled={isGenerating}
              className={`w-full px-4 py-3 rounded-xl border-2 resize-none ${
                darkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              } focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none transition-all`}
              rows={4}
            />

            <button
              onClick={generateTutorial}
              disabled={isGenerating || !prompt.trim()}
              className={`mt-4 w-full py-4 rounded-xl font-semibold flex items-center justify-center space-x-2 transition-all ${
                isGenerating || !prompt.trim()
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:scale-105'
              } text-white`}
            >
              {isGenerating ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  <span>Generate Tutorial</span>
                </>
              )}
            </button>

            {error && (
              <div className="mt-4 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                <p className="text-red-500">{error}</p>
              </div>
            )}
          </div>

          {/* Generation Progress */}
          {isGenerating && (
            <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <div className="flex items-center justify-between mb-3">
                <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {generationStatus}
                </p>
                {segmentFiles.length > 0 && generationProgress < 75 && (
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {segmentFiles.filter(f => f).length}/{totalSegments} complete
                  </p>
                )}
              </div>

              <div className="w-full bg-gray-300 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500"
                  style={{ width: `${generationProgress}%` }}
                />
              </div>

              <p className={`text-center mt-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {Math.round(generationProgress)}%
              </p>

              {/* Parallel Segment Progress Indicators */}
              {generationProgress >= 10 && generationProgress < 75 && (
                <div className="mt-4">
                  <p className={`text-xs font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    All segments generating in parallel:
                  </p>
                  <div className="grid grid-cols-5 gap-2">
                    {Array.from({ length: totalSegments }).map((_, i) => (
                      <div key={i} className="text-center">
                        <div
                          className={`h-12 rounded-lg flex items-center justify-center font-bold text-lg transition-all ${
                            segmentFiles[i]
                              ? 'bg-green-500 text-white'
                              : 'bg-yellow-500/30 border-2 border-yellow-500 text-yellow-600 animate-pulse'
                          } ${darkMode && !segmentFiles[i] ? 'text-yellow-400' : ''}`}
                        >
                          {i + 1}
                        </div>
                        <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {segmentFiles[i] ? '✓ Done' : 'Processing...'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Generated Videos */}
          {generatedVideos.length > 0 && (
            <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <h3 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Your Tutorials
              </h3>

              <div className="space-y-4">
                {generatedVideos.map(video => (
                  <div
                    key={video.id}
                    className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-white'}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {video.title}
                        </h4>
                        <div className="flex items-center space-x-4 mt-2">
                          <div className="flex items-center space-x-1 text-sm text-gray-500">
                            <Clock className="w-4 h-4" />
                            <span>{video.duration}s</span>
                          </div>
                          <div className="flex items-center space-x-1 text-sm text-green-500">
                            <CheckCircle className="w-4 h-4" />
                            <span>Complete</span>
                          </div>
                        </div>
                      </div>
                      <Video className="w-8 h-8 text-purple-500" />
                    </div>

                    {/* Video Player - show merged video if available, otherwise sequential player */}
                    {video.isMerged ? (
                      <div>
                        <video
                          src={video.url}
                          controls
                          className="w-full rounded-lg"
                        />
                        <div className="mt-3">
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Merged video • {video.duration}s continuous playback
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <SequentialVideoPlayer
                          segments={video.segments}
                          darkMode={darkMode}
                          videoId={video.id}
                        />

                        {/* Segment Indicators */}
                        {video.segments && video.segments.length > 1 && (
                          <div className="mt-3">
                            <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {video.segments.length} segments • {video.duration}s total
                            </p>
                            <div className="flex gap-1">
                              {video.segments.map((_, idx) => (
                                <div
                                  key={idx}
                                  className={`flex-1 h-1 rounded-full ${
                                    darkMode ? 'bg-purple-700' : 'bg-purple-300'
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Info Section */}
          <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-500/30' : 'bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-300'}`}>
            <h3 className={`font-semibold mb-2 ${darkMode ? 'text-purple-300' : 'text-purple-900'}`}>
              How it works:
            </h3>
            <ul className={`space-y-2 text-sm ${darkMode ? 'text-purple-200' : 'text-purple-800'}`}>
              <li className="flex items-start space-x-2">
                <span>•</span>
                <span>AI plans ONE cohesive lesson broken into {totalSegments} connected parts</span>
              </li>
              <li className="flex items-start space-x-2">
                <span>•</span>
                <span>Each part is a 12-second video that flows naturally to the next</span>
              </li>
              <li className="flex items-start space-x-2">
                <span>•</span>
                <span>All {totalSegments} parts play continuously as one seamless 1-minute tutorial</span>
              </li>
              <li className="flex items-start space-x-2">
                <span>•</span>
                <span>Professional educational video using OpenAI Sora AI</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AITutorialGenerator;
